import { logger } from '../logger';
import { isExcludeUa } from '../utils';
import {
  BUSINESS_LOGGER_KEYS,
  BUSINESS_LOGGER_LIMITS,
  SGM_DIMENSION_NAMES,
  type AllowedBusinessLoggerKey,
} from './constants';

// ============================================================================
// 类型定义
// ============================================================================

export interface BusinessLoggerOptions<T = undefined> {
  eventName: T extends Record<string, string> ? T[keyof T] : string;
  category?: Record<string, string>;
  value?: number;
  delay?: number;
  status?: boolean;
  message?: string | number | Record<string, string | number | boolean>; // 改进类型安全性
  pid?: string;
  useCustomLogger?: boolean;
  isReportSpiderLogs?: boolean; // 是否上报爬虫触发的上报
}

// 新增：Business Logger 实例接口
export interface BusinessLoggerInstance<T = undefined> {
  (options: BusinessLoggerOptions<T>): void;
}

interface BusinessInstance {
  labelNames(names: string[]): BusinessInstance;
  labels(values: string[]): {
    setMessage(msg: string): any;
    setValue(value: number): any;
    setStatus(status: boolean): any;
    setDelay(delay: number): any;
    submit(): boolean;
  };
}

interface GlobalObject {
  __sgm__?: {
    business?: (key: string, pid?: string) => BusinessInstance;
  };
}

// ============================================================================
// 工具函数
// ============================================================================

function validateString(value: string, name: string, maxLength: number): void {
  if (!value || typeof value !== 'string') {
    throw new Error(`${name} 必须是非空字符串`);
  }
  if (value.length > maxLength) {
    throw new Error(`${name} 长度不能超过${maxLength}个字符，当前长度：${value.length}`);
  }
}

function validateStringArray(
  values: string[],
  name: string,
  maxLength: number,
  itemMaxLength: number,
): string[] {
  if (!Array.isArray(values)) {
    throw new Error(`${name} 必须是数组`);
  }
  if (values.length > maxLength) {
    throw new Error(`${name} 数组长度不能超过${maxLength}个，当前长度：${values.length}`);
  }

  return values.map((value, index) => {
    if (!value || typeof value !== 'string') {
      value = ''; // 设置默认值为空字符串，而不是抛出错误
    }
    // 根据 SGM 规范，超长的维度值会被替换为预设值
    if (value.length > itemMaxLength) {
      console.warn(
        `[businessLogger] ${name}[${index}] 长度超过${itemMaxLength}个字符，可能被替换为SGM预设值`,
      );
      return value;
    }
    return value;
  });
}

function isBusinessLoggerKeyAllowed(key: string): key is AllowedBusinessLoggerKey {
  return BUSINESS_LOGGER_KEYS.includes(key as AllowedBusinessLoggerKey);
}

function getAllowedBusinessLoggerKeys(): string[] {
  return [...BUSINESS_LOGGER_KEYS];
}

function processMessage(message: string | number | Record<string, any>): string {
  let messageStr: string;

  if (typeof message === 'string') {
    messageStr = message;
  } else if (typeof message === 'number') {
    messageStr = String(message);
  } else if (typeof message === 'object' && message !== null) {
    // 对象类型转换为 JSON 字符串
    try {
      messageStr = JSON.stringify(message);
    } catch (error) {
      messageStr = String(message);
    }
  } else {
    // 其他类型直接转换为字符串（理论上不应该到达这里）
    messageStr = String(message);
  }

  // 检查长度限制
  return messageStr.length > BUSINESS_LOGGER_LIMITS.MESSAGE
    ? messageStr.substring(0, BUSINESS_LOGGER_LIMITS.MESSAGE)
    : messageStr;
}

function createLogData(options: BusinessLoggerOptions): any {
  return {
    eventName: options.eventName,
    category: options.category,
    value: options.value,
    delay: options.delay,
    status: options.status,
    message: options.message,
    pid: options.pid,
    timestamp: new Date().toISOString(),
  };
}

const templateInstances = new Map<string, BusinessInstance>();

/**
 * 简单的缓存清理：当超过最大大小时，清理一半
 */
function cleanupCache(): void {
  if (templateInstances.size <= BUSINESS_LOGGER_LIMITS.MAX_CACHE_SIZE) return;

  const entries = Array.from(templateInstances.keys());
  const deleteCount = Math.floor(entries.length / 2);

  // 删除前一半的缓存项（FIFO策略）
  for (let i = 0; i < deleteCount; i++) {
    templateInstances.delete(entries[i]);
  }
}

function getIsReport({ isReportSpiderLogs }: { isReportSpiderLogs?: boolean }) {
  if (isReportSpiderLogs) {
    return true;
  }
  // 是否是需要排除的ua
  if (isExcludeUa()) {
    return false;
  }
  return true;
}

/**
 * 创建预定义维度模板的 Business Logger
 *
 * 第一步：使用此函数预定义维度模板
 * 第二步：使用返回的实例进行埋点事件记录
 *
 * @param key - 业务监控的唯一标识符
 * @param categoryNames - 预定义的维度名称数组，如 ['page', 'dialog_type', 'user_action']
 * @returns 返回一个 BusinessLoggerInstance 实例，可直接调用进行埋点记录
 */
export function createBusinessLogger<T = undefined>(
  key: AllowedBusinessLoggerKey,
  categoryNames: string[],
): BusinessLoggerInstance<T> {
  // 验证参数
  const keyString = key as string;
  if (!isBusinessLoggerKeyAllowed(keyString)) {
    const allowedKeys = getAllowedBusinessLoggerKeys();
    throw new Error(
      `[createBusinessLogger] key "${keyString}" 不在允许的枚举值中。允许的值包括: ${allowedKeys.join(
        ', ',
      )}`,
    );
  }

  // 验证维度名称
  const validatedCategoryNames = validateStringArray(
    categoryNames,
    'categoryNames',
    BUSINESS_LOGGER_LIMITS.TOTAL_DIMENSIONS - 1,
    BUSINESS_LOGGER_LIMITS.CATEGORY_NAME,
  );

  // 创建模板实例的唯一标识
  const templateKey = `${keyString}_${validatedCategoryNames.join('_')}`;

  // 创建并返回 BusinessLoggerInstance 实例
  const loggerInstance: BusinessLoggerInstance<T> = (options: BusinessLoggerOptions<T>) => {
    try {
      const isReport = getIsReport({ isReportSpiderLogs: options?.isReportSpiderLogs });
      if (!isReport) {
        return;
      }

      // 验证必需参数
      validateString(options.eventName, 'eventName', BUSINESS_LOGGER_LIMITS.EVENT_NAME);

      // 验证 category 对象的键是否与预定义的维度名称匹配
      const providedKeys = Object.keys(options.category || {});
      const extraKeys = providedKeys.filter((name) => !validatedCategoryNames.includes(name));

      if (extraKeys.length > 0) {
        throw new Error(
          `[createBusinessLogger] category 包含未预定义的维度: ${extraKeys.join(', ')}`,
        );
      }

      // 按照预定义的顺序构建 category 值数组（缺少的维度使用空字符串）
      const categoryValues = validatedCategoryNames.map((name) => options.category?.[name] || '');

      // 验证 category 值
      const validatedCategoryValues = validateStringArray(
        categoryValues,
        'categoryValues',
        BUSINESS_LOGGER_LIMITS.TOTAL_DIMENSIONS - 1,
        BUSINESS_LOGGER_LIMITS.CATEGORY_VALUE,
      );

      // 执行 logger 上报
      if (options.useCustomLogger !== false) {
        logger.info({
          eventName: `${key}_${options.eventName}`,
          msg: {
            category: options.category,
            message: options.message,
            value: options.value,
            delay: options.delay,
            status: options.status,
          },
        });
      }

      // 简化的缓存管理：检查是否需要清理
      if (templateInstances.size > BUSINESS_LOGGER_LIMITS.MAX_CACHE_SIZE) {
        cleanupCache();
      }

      // 获取或创建 SGM 模板实例
      let sgmInstance: BusinessInstance;

      if (templateInstances.has(templateKey)) {
        sgmInstance = templateInstances.get(templateKey)!;
      } else {
        // 创建新的 SGM 实例并定义维度模板
        const finalCategoryNames = [SGM_DIMENSION_NAMES.EVENT_NAME, ...validatedCategoryNames];
        sgmInstance = window?.__sgm__
          ?.business(keyString, options?.pid)
          .labelNames(finalCategoryNames);
        sgmInstance && templateInstances.set(templateKey, sgmInstance);
      }

      // 构建最终的维度值数组（包含 eventName）
      const finalCategoryValues = [options.eventName, ...validatedCategoryValues];

      // 设置维度值并配置其他参数
      let labelsInstance = sgmInstance.labels(finalCategoryValues);

      // 设置可选参数
      if (options.value !== undefined) {
        labelsInstance = labelsInstance.setValue(options.value);
      }

      if (options.delay !== undefined) {
        labelsInstance = labelsInstance.setDelay(options.delay);
      }

      if (options.status !== undefined) {
        labelsInstance = labelsInstance.setStatus(options.status);
      }

      if (options.message !== undefined) {
        labelsInstance = labelsInstance.setMessage(processMessage(options.message));
      }

      // 提交上报
      const isSuccess = labelsInstance.submit();

      if (!isSuccess) {
        console.warn('[createBusinessLogger] SGM 上报失败');
      }
    } catch (error) {
      console.error('[createBusinessLogger] 记录业务监控失败:', error);
      logger.info({
        eventName: 'BUSINESS_LOGGER_ERROR',
        msg: {
          eventName: options.eventName,
          message: options.message,
          category: options.category,
          value: options.value,
          delay: options.delay,
          status: options.status,
        },
      });
    }
  };

  return loggerInstance;
}

/**
 * 简化版本的 logBusiness 函数
 * 直接传入完整参数进行埋点，内部会动态创建维度模板
 *
 * @param key - 业务监控的唯一标识符
 * @param options - 包含 eventName、category 等参数的对象
 */
export function logBusiness<T = undefined>(
  key: AllowedBusinessLoggerKey,
  options: BusinessLoggerOptions<T>,
): void {
  try {
    // 从 category 中提取维度名称
    const categoryNames = Object.keys(options.category || {});

    // 创建临时的 logger 实例
    const tempLoggerInstance = createBusinessLogger<T>(key, categoryNames);

    // 使用实例执行埋点
    tempLoggerInstance(options);
  } catch (error) {
    console.error('[logBusiness] 记录业务监控失败:', error);
  }
}
